import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { ArrowLeft, FileText, User, Calendar, UserCheck, Shield, ChevronsUpDown, Check, Layers } from 'lucide-react';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import axios from 'axios';
import DentistPicker from '../components/DentistPicker';
import { FormFillInFields } from '../components/FormFillInFields';
import { BundleFormPicker } from '../components/BundleFormPicker';

// Strip dashes/spaces/punctuation for fuzzy search ("xray" matches "X-Ray")
const normalizeForSearch = (s) =>
  (s || '').toLowerCase().replace(/[\s\-_/.,]+/g, '');

const AssignComplianceFormPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const preSelectedPatientId = searchParams.get('patientId');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [patients, setPatients] = useState([]);
  const [forms, setForms] = useState([]);              // Treatment Refusal + X-Ray Refusal + all informed consents
  const [procedures, setProcedures] = useState([]);    // Treatment list for the Refusal dropdown
  const [dentists, setDentists] = useState([]);        // Dentists in this practice
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formPopoverOpen, setFormPopoverOpen] = useState(false);
  const [treatmentPopoverOpen, setTreatmentPopoverOpen] = useState(false);
  const [patientPopoverOpen, setPatientPopoverOpen] = useState(false);
  const [mode, setMode] = useState('single');          // 'single' | 'bundle'
  const [bundleIds, setBundleIds] = useState([]);
  const [fieldValues, setFieldValues] = useState({});  // { consentId: { 'section:idx': value } }
  const setFormFieldValues = (id, v) => setFieldValues((prev) => ({ ...prev, [id]: v }));

  const [formData, setFormData] = useState({
    patientId: preSelectedPatientId || '',
    consentId: '',
    assignedDate: new Date().toISOString().split('T')[0],
    dentistId: '',         // selected from dropdown
    dentistName: '',       // resolved name (sent to backend)
    practiceNotes: '',
    treatmentRefused: '',
  });

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      setLoading(true);
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } };

      const [patientsResponse, consentsResponse, proceduresResponse, mgmtFormsResponse, dentistsResponse] = await Promise.all([
        practiceApi.getPatients(),
        axios.get(`${backendUrl}/api/informed-consent/list`, authHeader),
        axios.get(`${backendUrl}/api/practice/procedures`, authHeader).catch(() => ({ data: { procedures: [] } })),
        axios.get(`${backendUrl}/api/management-forms`, authHeader).catch(() => ({ data: { data: [] } })),
        axios.get(`${backendUrl}/api/practice/dentists`, authHeader).catch(() => ({ data: { data: [] } })),
      ]);

      setPatients(patientsResponse.data || []);
      setForms(consentsResponse.data.data || []);

      const dentistList = dentistsResponse.data?.data || [];
      // Sort by last name
      dentistList.sort((a, b) =>
        (a.lastName || '').localeCompare(b.lastName || '')
      );
      setDentists(dentistList);

      // Auto-select the only dentist if there's exactly one
      if (dentistList.length === 1) {
        const d = dentistList[0];
        const fullName = `Dr. ${d.firstName} ${d.lastName}`.trim();
        setFormData((prev) => ({ ...prev, dentistId: d.id, dentistName: fullName }));
      }

      // Build the unified treatment list for the Refusal-form dropdown
      const procPayload = proceduresResponse.data || {};
      const procList = Array.isArray(procPayload) ? procPayload : (procPayload.procedures || procPayload.data || []);
      const mgmtPayload = mgmtFormsResponse.data || {};
      const mgmtList = Array.isArray(mgmtPayload) ? mgmtPayload : (mgmtPayload.data || []);

      const combined = [
        ...procList.map((p) => ({ id: `proc:${p.id}`, name: p.name || '', source: 'Post-Treatment Forms' })),
        ...mgmtList.map((f) => ({ id: `mgmt:${f.id}`, name: f.title || f.name || '', source: 'Compliance / Office Forms' })),
      ].filter((x) => x.name);

      // Dedupe by normalized name
      const seen = new Set();
      const deduped = [];
      for (const item of combined) {
        const key = normalizeForSearch(item.name);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(item);
      }
      deduped.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProcedures(deduped);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load required data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Dentist picker callback — called from <DentistPicker>
  const handleDentistChange = ({ id, name }) => {
    handleInputChange('dentistId', id);
    handleInputChange('dentistName', name);
  };

  const handleDentistAdded = (newDentistObj) => {
    setDentists((prev) => {
      const without = prev.filter((d) => d.id !== newDentistObj.id);
      return [...without, newDentistObj].sort((a, b) =>
        (a.lastName || '').localeCompare(b.lastName || '')
      );
    });
  };

  const selectedForm = forms.find((c) => c.id === formData.consentId);
  const isBundle = mode === 'bundle';
  const bundleForms = bundleIds.map((id) => forms.find((c) => c.id === id)).filter(Boolean);
  const isTreatmentRefusalForm = isBundle
    ? bundleForms.some((c) => c.isRefusalForm)
    : !!selectedForm?.isRefusalForm;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patientId) newErrors.patientId = 'Please select a patient';
    if (isBundle && bundleIds.length < 2) newErrors.consentId = 'Select at least two forms for a bundle';
    if (!isBundle && !formData.consentId) newErrors.consentId = 'Please select a compliance form';
    if (!formData.assignedDate) newErrors.assignedDate = 'Please select a date';
    if (!formData.dentistName?.trim()) newErrors.dentistName = 'Please select a dentist';
    if (isTreatmentRefusalForm && !formData.treatmentRefused?.trim()) {
      newErrors.treatmentRefused = 'Please select the treatment being refused';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const authCfg = { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } };
      if (isBundle) {
        await axios.post(`${backendUrl}/api/informed-consent/assign-bundle`, {
          patientId: formData.patientId,
          assignedDate: new Date(formData.assignedDate).toISOString(),
          dentistName: formData.dentistName,
          practiceNotes: formData.practiceNotes || '',
          items: bundleForms.map((c) => ({
            consentId: c.id,
            consentName: c.name,
            treatmentRefused: c.isRefusalForm ? (formData.treatmentRefused || '').trim() : null,
            fieldValues: fieldValues[c.id] || {},
          })),
        }, authCfg);
        toast({ title: 'Success', description: `${bundleForms.length} forms sent as one signing link` });
        navigate('/');
        return;
      }
      const payload = {
        patientId: formData.patientId,
        consentId: formData.consentId,
        consentName: selectedForm?.name || '',
        assignedDate: new Date(formData.assignedDate).toISOString(),
        dentistName: formData.dentistName,
        practiceNotes: formData.practiceNotes || '',
        treatmentRefused: isTreatmentRefusalForm ? (formData.treatmentRefused || '').trim() : null,
        fieldValues: fieldValues[formData.consentId] || {},
      };

      await axios.post(`${backendUrl}/api/informed-consent/assign`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` },
      });

      toast({ title: 'Success', description: 'Compliance form assigned successfully' });
      navigate('/');
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to assign compliance form',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const selectedPatient = patients.find((p) => p.id === formData.patientId);

  // Group forms: Treatment Refusal + X-Ray Refusal + Periodontal Refusal.
  // Informed Consents are NOT shown here — they have their own dedicated
  // Assign Informed Consent page (separate function).
  const refusalForms = forms.filter((c) => c.isRefusalForm);
  const xrayForms = forms.filter((c) => c.isXrayRefusalForm);
  const perioForms = forms.filter((c) => c.isPerioRefusalForm);
  const failureForms = forms.filter((c) => c.isFailureToCompleteForm);
  const patientForms = forms
    .filter((c) => c.isPatientComplianceForm)
    .sort((a, b) => (a.complianceOrder || 0) - (b.complianceOrder || 0));
  const formGroups = [
    { heading: 'Treatment Refusal', forms: refusalForms },
    { heading: 'X-Ray Refusal', forms: xrayForms },
    { heading: 'Periodontal Refusal', forms: perioForms },
    { heading: 'Failure to Complete Treatment', forms: failureForms },
    { heading: 'Patient Compliance Forms', forms: patientForms },
  ].filter((g) => g.forms.length);

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="assign-compliance-form-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-amber-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Assign Compliance Form</h1>
              <p className="text-gray-600 mt-1">
                X-Ray Refusal or Patient Treatment Refusal — auto-emailed and signed by the patient
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Assignment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Patient picker — searchable combobox */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>Patient *</span>
                </Label>
                <Popover open={patientPopoverOpen} onOpenChange={setPatientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={patientPopoverOpen}
                      data-testid="compliance-patient-trigger"
                      className={cn(
                        'w-full bg-white border rounded-md px-3 py-2 text-sm flex items-center justify-between gap-2 h-10',
                        errors.patientId ? 'border-red-500' : 'border-gray-300',
                        !formData.patientId && 'text-gray-500'
                      )}
                    >
                      <span className="truncate">
                        {formData.patientId
                          ? (() => {
                              const p = patients.find((pt) => pt.id === formData.patientId);
                              return p ? `${p.firstName} ${p.lastName}${p.email ? ` (${p.email})` : ''}` : 'Selected';
                            })()
                          : patients.length === 0
                          ? 'Loading…'
                          : 'Search or select a patient…'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                    <Command
                      filter={(value, search) => {
                        const v = normalizeForSearch(value);
                        const s = normalizeForSearch(search);
                        return v.includes(s) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search patients by name or email…" data-testid="compliance-patient-search-input" />
                      <CommandList>
                        <CommandEmpty>No matching patient found.</CommandEmpty>
                        <CommandGroup>
                          {patients.map((p) => (
                            <CommandItem
                              key={p.id}
                              value={`${p.firstName} ${p.lastName} ${p.email || ''}`}
                              onSelect={() => {
                                handleInputChange('patientId', p.id);
                                setPatientPopoverOpen(false);
                              }}
                              data-testid={`compliance-patient-option-${p.id}`}
                            >
                              <Check className={cn('mr-2 h-4 w-4', formData.patientId === p.id ? 'opacity-100' : 'opacity-0')} />
                              <div className="flex flex-col flex-1">
                                <span className="font-medium">
                                  {p.firstName} {p.lastName}
                                </span>
                                {p.email && <span className="text-xs text-gray-500">{p.email}</span>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.patientId && <p className="text-sm text-red-600">{errors.patientId}</p>}
              </div>

              {/* Single form vs bundle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg" data-testid="assign-mode-toggle">
                {[['single', 'Single Form', FileText], ['bundle', 'Form Bundle', Layers]].map(([m, label, Icon]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMode(m); setErrors({}); }}
                    className={cn('flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors',
                      mode === m ? 'bg-white shadow text-amber-700' : 'text-gray-600 hover:text-gray-900')}
                    data-testid={`assign-mode-${m}`}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>

              {isBundle && (
                <div className="space-y-2">
                  <Label className="flex items-center space-x-2">
                    <Layers className="h-4 w-4" />
                    <span>Forms in Bundle *</span>
                  </Label>
                  <p className="text-xs text-gray-500">The patient gets one link and signs each form in the order shown.</p>
                  <BundleFormPicker
                    groups={formGroups}
                    selectedIds={bundleIds}
                    onChange={(ids) => { setBundleIds(ids); setErrors((e) => ({ ...e, consentId: undefined })); }}
                    fieldValues={fieldValues}
                    onFieldValuesChange={setFormFieldValues}
                    error={errors.consentId}
                  />
                  {errors.consentId && <p className="text-sm text-red-600" data-testid="bundle-error">{errors.consentId}</p>}
                </div>
              )}

              {/* Compliance Form picker */}
              {!isBundle && (
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Compliance Form *</span>
                </Label>
                <Popover open={formPopoverOpen} onOpenChange={setFormPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={formPopoverOpen}
                      data-testid="compliance-form-trigger"
                      className={cn(
                        'w-full bg-white border rounded-md px-3 py-2 text-sm flex items-center justify-between gap-2 h-10',
                        errors.consentId ? 'border-red-500' : 'border-gray-300',
                        !formData.consentId && 'text-gray-500'
                      )}
                    >
                      <span className="truncate">
                        {formData.consentId
                          ? selectedForm?.name || 'Selected'
                          : forms.length === 0
                          ? 'Loading…'
                          : 'Search or select a compliance form…'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                    <Command
                      filter={(value, search) => {
                        const v = normalizeForSearch(value);
                        const s = normalizeForSearch(search);
                        return v.includes(s) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search forms… (e.g. xray, refusal)" data-testid="compliance-form-search-input" />
                      <CommandList>
                        <CommandEmpty>No matching form found.</CommandEmpty>
                        {refusalForms.length > 0 && (
                          <CommandGroup heading="Treatment Refusal">
                            {refusalForms.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} treatment refusal denial`}
                                onSelect={() => {
                                  handleInputChange('consentId', c.id);
                                  setFormData((prev) => ({ ...prev, treatmentRefused: '' }));
                                  setFormPopoverOpen(false);
                                }}
                                data-testid={`compliance-form-option-${c.id}`}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.consentId === c.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="flex-1">{c.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {xrayForms.length > 0 && (
                          <CommandGroup heading="X-Ray Refusal">
                            {xrayForms.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} xray x-ray refusal radiograph`}
                                onSelect={() => {
                                  handleInputChange('consentId', c.id);
                                  setFormData((prev) => ({ ...prev, treatmentRefused: '' }));
                                  setFormPopoverOpen(false);
                                }}
                                data-testid={`compliance-form-option-${c.id}`}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.consentId === c.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="flex-1">{c.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {perioForms.length > 0 && (
                          <CommandGroup heading="Periodontal Refusal">
                            {perioForms.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} periodontal perio gum disease refusal`}
                                onSelect={() => {
                                  handleInputChange('consentId', c.id);
                                  setFormData((prev) => ({ ...prev, treatmentRefused: '' }));
                                  setFormPopoverOpen(false);
                                }}
                                data-testid={`compliance-form-option-${c.id}`}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.consentId === c.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="flex-1">{c.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {failureForms.length > 0 && (
                          <CommandGroup heading="Failure to Complete Treatment">
                            {failureForms.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} failure to complete incomplete treatment notice`}
                                onSelect={() => {
                                  handleInputChange('consentId', c.id);
                                  setFormData((prev) => ({ ...prev, treatmentRefused: '' }));
                                  setFormPopoverOpen(false);
                                }}
                                data-testid={`compliance-form-option-${c.id}`}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.consentId === c.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="flex-1">{c.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {patientForms.length > 0 && (
                          <CommandGroup heading="Patient Compliance Forms">
                            {patientForms.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} patient compliance form`}
                                onSelect={() => {
                                  handleInputChange('consentId', c.id);
                                  setFormData((prev) => ({ ...prev, treatmentRefused: '' }));
                                  setFormPopoverOpen(false);
                                }}
                                data-testid={`compliance-form-option-${c.id}`}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.consentId === c.id ? 'opacity-100' : 'opacity-0')} />
                                <span className="flex-1">{c.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.consentId && <p className="text-sm text-red-600">{errors.consentId}</p>}
              </div>
              )}

              {!isBundle && formData.consentId && (
                <FormFillInFields
                  key={formData.consentId}
                  consentId={formData.consentId}
                  values={fieldValues[formData.consentId] || {}}
                  onChange={(v) => setFormFieldValues(formData.consentId, v)}
                />
              )}

              {/* Treatment-Refused dropdown — only for the Patient Treatment Refusal form */}
              {isTreatmentRefusalForm && (
                <div className="space-y-2 p-4 border-2 border-amber-300 bg-amber-50 rounded-lg" data-testid="treatment-refused-section">
                  <Label className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-amber-700" />
                    <span className="text-amber-900 font-semibold">Treatment Being Refused *</span>
                  </Label>
                  <p className="text-xs text-amber-800">
                    Search for the specific treatment this patient is refusing — it will be inserted into the document, email, SMS, and signed PDF.
                  </p>
                  <Popover open={treatmentPopoverOpen} onOpenChange={setTreatmentPopoverOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={treatmentPopoverOpen}
                        data-testid="treatment-refused-trigger"
                        className={cn(
                          'w-full bg-white border rounded-md px-3 py-2 text-sm flex items-center justify-between gap-2 h-10',
                          errors.treatmentRefused ? 'border-red-500' : 'border-gray-300',
                          !formData.treatmentRefused && 'text-gray-500'
                        )}
                      >
                        <span className="truncate">
                          {formData.treatmentRefused ||
                            (procedures.length === 0 ? 'Loading treatments…' : 'Select or search for a treatment…')}
                        </span>
                        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                      <Command
                        filter={(value, search) => {
                          const v = normalizeForSearch(value);
                          const s = normalizeForSearch(search);
                          return v.includes(s) ? 1 : 0;
                        }}
                      >
                        <CommandInput placeholder="Search treatments… (e.g. xray, wisdom, implant)" data-testid="treatment-refused-search-input" />
                        <CommandList>
                          <CommandEmpty>No matching treatment found.</CommandEmpty>
                          {(() => {
                            const groups = procedures.reduce((acc, p) => {
                              const key = p.source || 'Treatments';
                              (acc[key] = acc[key] || []).push(p);
                              return acc;
                            }, {});
                            return Object.entries(groups).map(([groupName, items]) => (
                              <CommandGroup key={groupName} heading={groupName}>
                                {items.map((proc) => (
                                  <CommandItem
                                    key={proc.id}
                                    value={proc.name}
                                    onSelect={(currentValue) => {
                                      handleInputChange('treatmentRefused', currentValue);
                                      setTreatmentPopoverOpen(false);
                                    }}
                                    data-testid={`treatment-option-${proc.id}`}
                                  >
                                    <Check className={cn('mr-2 h-4 w-4', formData.treatmentRefused === proc.name ? 'opacity-100' : 'opacity-0')} />
                                    {proc.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            ));
                          })()}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {errors.treatmentRefused && (
                    <p className="text-sm text-red-600" data-testid="treatment-refused-error">
                      {errors.treatmentRefused}
                    </p>
                  )}
                </div>
              )}

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>Date *</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.assignedDate}
                  onChange={(e) => handleInputChange('assignedDate', e.target.value)}
                  className={errors.assignedDate ? 'border-red-500' : ''}
                  data-testid="compliance-date-input"
                />
                {errors.assignedDate && <p className="text-sm text-red-600">{errors.assignedDate}</p>}
              </div>

              {/* Dentist — dropdown of practice dentists with add-on-the-fly + cell warning */}
              <div className="space-y-2">
                <Label htmlFor="dentist" className="flex items-center space-x-2">
                  <UserCheck className="h-4 w-4" />
                  <span>Dentist *</span>
                </Label>
                <DentistPicker
                  dentists={dentists}
                  selectedId={formData.dentistId}
                  onChange={handleDentistChange}
                  onDentistAdded={handleDentistAdded}
                  error={errors.dentistName}
                  testIdPrefix="compliance"
                />
                {errors.dentistName && <p className="text-sm text-red-600">{errors.dentistName}</p>}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Practice Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.practiceNotes}
                  onChange={(e) => handleInputChange('practiceNotes', e.target.value)}
                  placeholder="Any internal notes for this assignment…"
                  rows={3}
                  data-testid="compliance-notes-input"
                />
              </div>

              {/* Summary */}
              {selectedPatient && isBundle && bundleForms.length > 0 && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="bundle-summary">
                  <h3 className="font-semibold text-gray-900 mb-2">Bundle Summary</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Patient:</strong> {selectedPatient.firstName} {selectedPatient.lastName}</p>
                    <ol className="list-decimal ml-5">
                      {bundleForms.map((c) => <li key={c.id}>{c.name}</li>)}
                    </ol>
                    <p><strong>Provider:</strong> {formData.dentistName}</p>
                  </div>
                </div>
              )}

              {selectedPatient && !isBundle && selectedForm && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="compliance-summary">
                  <h3 className="font-semibold text-gray-900 mb-2">Assignment Summary</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>Patient:</strong> {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                    <p>
                      <strong>Form:</strong> {selectedForm.name}
                      {isTreatmentRefusalForm && formData.treatmentRefused && (
                        <span>
                          {' — '}
                          <span className="text-amber-700 font-semibold">{formData.treatmentRefused}</span>
                        </span>
                      )}
                    </p>
                    <p><strong>Date:</strong> {new Date(formData.assignedDate).toLocaleDateString()}</p>
                    <p><strong>Provider:</strong> {formData.dentistName}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="assign-compliance-form-submit"
                >
                  {submitting ? 'Assigning…' : isBundle ? `Send Bundle (${bundleForms.length} forms)` : 'Assign Compliance Form'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/')}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssignComplianceFormPage;
